<template>
    <div>
        <BreadcrumbFolder :firstTitle="$t('table_title.User_Folder')" />
        <a-row :gutter="24" type="flex">
            <a-col :span="24" class="mb-24">
                <a-card :bordered="false" class="header-solid h-full" :bodyStyle="{ padding: 0 }">
                    <template #title>
                        <a-row type="flex" align="middle">
                            <a-col :span="24" :md="12">
                                <h5 class="font-semibold m-0">{{ $t('table_title.User_Folder') }}</h5>
                            </a-col>
                        </a-row>
                    </template>

                    <template>
                        <v-data-table
                            :headers="hiddenColTable"
                            :items="userFolders"
                            :page="page"
                            :no-data-text="$t('cms.no_data_available')"
                            :loading="isLoadingUserFolder"
                            :loading-text="$t('cms.loading')"
                            class="elevation-1"
                            hide-default-footer
                        >
                            <template v-slot:top>
                                <v-toolbar flat>
                                    <v-dialog v-model="dialogCU" persistent max-width="500px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn color="primary" dark class="mr-2" @click="onAddNewItem()" small>
                                                <v-icon small> mdi-plus </v-icon>
                                                {{ $t('cms.add_new') }}
                                            </v-btn>
                                        </template>
                                        <UserFolderModal
                                            :cuItem="cuItem"
                                            :formTitle="formTitle"
                                            :closeCU="closeCU"
                                            :save="onSaveItem"
                                            :propsField="propsField"
                                            :columns="tableColumns"
                                            :dialogEdit="dialogEdit"
                                            v-if="dialogCU"
                                        >
                                        </UserFolderModal>
                                    </v-dialog>

                                    <v-spacer></v-spacer>

                                    <div class="search">
                                        <a-select :default-value="selectedField" @change="handleChangeSelect">
                                            <a-select-option
                                                v-for="(item, index) in fieldsSearch"
                                                :key="index"
                                                :value="item.value"
                                            >
                                                {{ item.title }}
                                            </a-select-option>
                                        </a-select>
                                        <a-input-search
                                            v-model="search"
                                            :placeholder="$t('header.search')"
                                            @change="onSearchItem"
                                        />
                                    </div>

                                    <v-dialog v-model="dialogDelete" max-width="500px">
                                        <ModalDelete :closeDelete="closeDelete" :deleteItemConfirm="deleteItemConfirm">
                                        </ModalDelete>
                                    </v-dialog>

                                    <v-dialog v-model="dialogNoti" max-width="500px">
                                        <ModalNoti :close="closeNoti" :message="formMessage"></ModalNoti>
                                    </v-dialog>
                                </v-toolbar>
                            </template>
                            <template v-slot:[`item.icon`]="{ item }">
                                <div class="my-2">
                                    <v-icon class="fa-solid fa-folder" :style="{ fontSize: '18px' }"></v-icon>
                                </div>
                            </template>
                            <template v-slot:[`item.title`]="{ item }">
                                <div class="my-2 line-clamp-2">
                                    <span>{{ item.title || '-' }}</span>
                                </div>
                            </template>
                            <template v-slot:[`item.created_at_unix_timestamp`]="{ item }">
                                <div class="my-2">
                                    <span>{{ getFormatDate(+item.created_at_unix_timestamp) }}</span>
                                </div>
                            </template>
                            <template v-slot:[`item.updated_at_unix_timestamp`]="{ item }">
                                <div class="my-2">
                                    <span>{{ getFormatDate(+item.updated_at_unix_timestamp) }}</span>
                                </div>
                            </template>
                            <template v-slot:[`item.actions`]="{ item }">
                                <router-link :to="$router.resolve({ path: `/user_folder?folder_id=${item.id}` }).href">
                                    <v-btn color="indigo" class="mr-2" dark small>
                                        <v-icon left> mdi mdi-format-list-bulleted-type </v-icon>
                                        {{ $t('cms.detail') }}
                                    </v-btn>
                                </router-link>
                                <v-btn color="info" class="mr-2" small @click="onUpdateItem(item)">
                                    <v-icon small> mdi-pencil </v-icon>
                                    &nbsp;{{ $t('form.edit') }}
                                </v-btn>
                                <v-btn
                                    color="error"
                                    dark
                                    small
                                    @click="onDeleteItem(item)"
                                    v-if="item.id !== userInfo.id"
                                >
                                    <v-icon small> mdi-delete </v-icon>
                                    &nbsp;{{ $t('form.delete') }}
                                </v-btn>
                            </template>
                        </v-data-table>
                        <div class="text-center py-5">
                            <f-pagination
                                v-model="page"
                                :pageSize="userFoldersPagination.pageSize"
                                :totalItems="userFoldersPagination.totalItems"
                                :disabled="isLoadingUserFolder"
                            />
                        </div>
                    </template>
                </a-card>
            </a-col>
        </a-row>
        <UserFolderItem v-if="folder_id"></UserFolderItem>
    </div>
</template>

<script>
import _ from 'lodash';

import { mapState, mapActions } from 'vuex';

import { CRUD } from '../../constants';
import { mixinFormats, mixinToasts, mixinLoadings } from '../../mixins';

import User from './User.vue';
import UserFolderItem from './UserFolderItem.vue';
import UserFolderModal from '../../components/CMS/Modal/Folder.vue';
import ModalDelete from '../../components/CMS/Modal/Delete.vue';
import ModalNoti from '../../components/CMS/Modal/Noti.vue';
import BreadcrumbFolder from '../../components/CMS/BreadcrumbFolder';

export default {
    mixins: [mixinFormats, mixinToasts, mixinLoadings],
    components: { User, UserFolderModal, ModalDelete, ModalNoti, UserFolderItem, BreadcrumbFolder },
    data() {
        return {
            tableTitle: this.$t('cms.user_table'),
            tableColumns: [
                { text: '', value: 'icon', sortable: false, width: '50px' },
                { text: this.$t('form.title'), value: 'title', sortable: false, width: '300px' },
                {
                    text: this.$t('form.created_at'),
                    value: 'created_at_unix_timestamp',
                    sortable: false,
                    width: '200px',
                },
                {
                    text: this.$t('form.updated_at'),
                    value: 'updated_at_unix_timestamp',
                    sortable: false,
                    width: '200px',
                },
                {
                    text: this.$t('cms.action'),
                    value: 'actions',
                    width: '300px',
                    sortable: false,
                },
            ],
            fieldsSearch: [
                {
                    title: this.$t('form.title'),
                    value: 'title',
                },
            ],
            selectedField: 'title',
            search: '',
            page: 1,
            cuIndex: -1,
            cuItem: {},
            cuItemDelete: {},
            dialogCU: false,
            dialogDelete: false,
            dialogNoti: false,
            dialogEdit: false,
            dialogCUUser: false,
            dialogEditUser: false,
            noImage: require('../../assets/images/no_image.jpg'),
            formMessage: '',
            userInfo: this.$store.getters.getUserInfo,
            selectUser: [],

            // FOLDER PARENT ID
            folder_id: null,
        };
    },

    created() {
        this.hiddenFieldModal(this.dialogEdit);
    },

    computed: {
        ...mapState('userFolder', ['isLoadingUserFolder', 'userFolders', 'userFoldersPagination']),
        formTitle() {
            return this.cuIndex === -1 ? this.$t('action.new_folder') : this.$t('action.edit_folder');
        },
        hiddenColTable() {
            return this.tableColumns.filter((s) => s.value !== 'password');
        },
    },

    async mounted() {
        this.folder_id = this.$route.query.folder_id || null;
        await this.getUserFolderList({ page: 1, field: this.selectedField, search: this.search });
    },

    watch: {
        page(val) {
            this.getUserFolderList({ page: val, field: this.selectedField, search: this.search });
        },
        dialogCU(val) {
            val || this.closeCU();
        },
        dialogDelete(val) {
            val || this.closeDelete();
        },
        dialogNoti(val) {
            val || this.closeNoti();
        },
        dialogEdit(val) {
            this.hiddenFieldModal(val);
        },
        $route: function () {
            this.folder_id = this.$route.query?.folder_id || null;
            this.getUserFolderList({ page: 1, field: this.selectedField, search: this.search });
        },
    },

    methods: {
        ...mapActions('userFolder', ['getUserFolders', 'postNewUserFolder', 'putOneUserFolder', 'deleteOneUserFolder']),

        async getUserFolderList(data) {
            this.page = data?.page || 1;
            this.selectedField = data?.field || '';
            this.search = data?.search || '';
            await this.getUserFolders({ ...data, folder_id: this.folder_id });
        },

        hiddenFieldModal(val) {
            if (val === true) {
                this.propsField = this.tableColumns.filter(
                    (s) => s.value !== 'password' && s.value !== 'created_at' && s.value !== 'updated_at',
                );
            } else {
                this.propsField = this.tableColumns.filter((s) => s.value !== 'created_at' && s.value !== 'updated_at');
            }
        },
        onAddNewItem() {
            this.dialogEdit = false;
            this.dialogCU = true;
        },
        onUpdateItem(item) {
            this.dialogEdit = true;
            this.dialogCU = true;
            this.cuIndex = this.userFolders.indexOf(item);
            this.cuItem = Object.assign({}, item);
        },
        onDeleteItem(item) {
            this.cuIndex = this.userFolders.indexOf(item);
            this.cuItemDelete = Object.assign({}, item);
            this.dialogDelete = true;
        },
        async deleteItemConfirm() {
            this.closeDelete();
            this.showLoading();
            const res = await this.deleteOneUserFolder(this.cuItemDelete.id);
            if (res?.isSuccess) {
                this.showSuccess(CRUD.DELETE);
                await this.getUserFolderList({ page: this.page, field: this.selectedField, search: this.search });
            } else {
                this.showError(res?.data);
            }
            this.hideLoading();
        },
        closeCU() {
            this.dialogCU = false;
            this.$nextTick(() => {
                this.cuItem = Object.assign({}, this.defaultItem);
                this.cuIndex = -1;
            });
        },
        closeDelete() {
            this.dialogDelete = false;
            this.$nextTick(() => {
                this.cuItem = Object.assign({}, this.defaultItem);
                this.cuIndex = -1;
            });
        },
        closeNoti() {
            this.dialogNoti = false;
        },
        async onSaveItem(callback) {
            this.showLoading();
            if (this.cuIndex === -1) {
                let res = null;
                if (this.folder_id) {
                    res = await this.postNewUserFolder({
                        ...this.cuItem,
                        parent_id: this.folder_id,
                    });
                } else {
                    res = await this.postNewUserFolder({
                        ...this.cuItem,
                    });
                }
                callback(res?.isSuccess);
                if (res?.isSuccess) {
                    this.showSuccess(CRUD.CREATE);
                    await this.getUserFolderList({ page: 1, field: 'title', search: '' });
                } else {
                    this.showError(res?.data);
                }
            } else {
                let res = null;
                if (this.folder_id) {
                    res = await this.putOneUserFolder({
                        ...this.cuItem,
                        parent_id: this.folder_id,
                    });
                } else {
                    res = await this.putOneUserFolder({
                        ...this.cuItem,
                    });
                }
                callback(res?.isSuccess);
                if (res?.isSuccess) {
                    this.showSuccess(CRUD.UPDATE);
                    await this.getUserFolderList({ page: this.page, field: this.selectedField, search: this.search });
                } else {
                    this.showError(res?.data);
                }
            }
            this.hideLoading();
        },
        onSearchItem: _.debounce(function (e) {
            this.getUserFolderList({ page: 1, field: this.selectedField, search: this.search });
        }, 500),
        handleChangeSelect(value) {
            this.getUserFolderList({ page: 1, field: value, search: this.search });
        },

        onAddUser() {
            this.dialogEditUser = false;
            this.dialogCUUser = true;
        },
        closeCUUser() {
            this.dialogCUUser = false;
            this.$nextTick(() => {
                this.cuItemUser = Object.assign({}, this.defaultItem);
                this.cuIndex = -1;
            });
        },
    },
};
</script>

<style lang="scss">
.table-image {
    width: 45px;
    height: 45px;
}

.search {
    & .ant-select {
        margin-right: 10px;
        width: 120px;
    }

    & .ant-input-search {
        width: 500px;
    }
}
</style>
